import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { trackRumAction } from "@/lib/datadog";
import { ArrowUpDown, Download, Table2, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AbstractInset, { BaseInsetProps, InsetState } from "./AbstractInset";
import {
  VariableSelectionDropdown,
  type VariableOption,
} from "./VariableSelectionDropdown";
import BoxAndWhiskerPlot, {
  BoxPlotDataPoint,
  BoxPlotSeries,
} from "@/components/plots/BoxAndWhiskerPlot";

// Interface for displaying table rows
export interface MapTableInsetRow {
  rowId?: string;
  rowName: string;
  rowSlug?: string;
  rowData: {
    variableId: string;
    variableName: string;
    variableSlug: string;
    variableUnits?: string;
    variableType?: string;
    variableSummary?: string;
    variableOrder?: number;
    variableValue: number | string | Date | null | undefined;
  }[];
}

interface MapCombinedForecastInsetProps extends BaseInsetProps {
  data: MapTableInsetRow[];
  // Table-specific props
  onRowClick?: (item: any) => void;
  maxVisibleColumns?: number;
  maxColumnWidth?: number;
  availableVariables?: VariableOption[];
  selectedVariables?: string[];
  onSelectedVariablesChange?: (variables: string[]) => void;
  formatVariableName?: (slug: string) => string;
  aggregationRow?: MapTableInsetRow | null;
  csvMetadata?: {
    weatherModel?: string;
    validDate?: Date | null;
    modelRunDate?: Date | null;
    predictionType?: string;
  };
  // BoxPlot-specific props
  yAxisTitle?: string;
  onBoxPlotClick?: (item: any) => void;
  // Tab control
  defaultTab?: "table" | "boxplot";
}

/**
 * MapCombinedForecastInset Component
 *
 * A collapsible inset that combines a data table and box plot visualization.
 * Uses tabs to switch between "Forecast at a Glance" (table) and "Forecast Uncertainty" (box plot).
 */
const MapCombinedForecastInset: React.FC<MapCombinedForecastInsetProps> = ({
  data,
  loading = false,
  error = null,
  position = "top-left",
  width = "520px",
  height = "420px",
  title = "Forecast Overview",
  subtitle,
  onRowClick,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  areaNameKey = "rowName",
  maxVisibleColumns = 5,
  maxColumnWidth = 200,
  availableVariables = [],
  selectedVariables,
  onSelectedVariablesChange,
  formatVariableName,
  aggregationRow,
  csvMetadata,
  yAxisTitle = "Customers",
  defaultTab = "table",
  panelMode = false,
  isDraggable = true,
}) => {
  const [activeTab, setActiveTab] = useState<"table" | "boxplot">(defaultTab);
  const currentInsetContent = {
    data,
    subtitle,
    availableVariables,
    selectedVariables,
    aggregationRow,
    csvMetadata,
    yAxisTitle,
  };
  const committedInsetContentRef = useRef(currentInsetContent);

  if (!loading) {
    committedInsetContentRef.current = currentInsetContent;
  }

  const hasCommittedInsetContent =
    committedInsetContentRef.current.data.length > 0 ||
    committedInsetContentRef.current.availableVariables.length > 0 ||
    !!committedInsetContentRef.current.aggregationRow;
  const shouldPreserveInsetContent = loading && hasCommittedInsetContent;
  const insetContent = shouldPreserveInsetContent
    ? committedInsetContentRef.current
    : currentInsetContent;
  const displayData = insetContent.data;
  const displaySubtitle = insetContent.subtitle;
  const displayAvailableVariables = insetContent.availableVariables;
  const displaySelectedVariables = insetContent.selectedVariables;
  const displayAggregationRow = insetContent.aggregationRow;
  const displayCsvMetadata = insetContent.csvMetadata;
  const displayYAxisTitle = insetContent.yAxisTitle;

  const pageSize = 10;

  const isResizingRef = useRef(false);
  const cachedChartDataRef = useRef<{
    series: BoxPlotSeries[];
    options: any;
  } | null>(null);

  const [triggerChartRedraw, setTriggerChartRedraw] = useState(false);

  const [dataChangeKey, setDataChangeKey] = useState(0);

  const [filter, setFilter] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
    setFilter("");
    setDataChangeKey((prev) => prev + 1);
  }, [displayData]);

  const responsiveDimensions = useMemo(() => {
    const dataLength = displayData?.length || 0;

    const baseWidth = parseInt(width.replace("px", "")) || 520;
    const baseHeight = parseInt(height.replace("px", "")) || 420;

    let scaleFactor = 1;

    if (dataLength > 30) {
      scaleFactor = 1.2;
    } else if (dataLength > 20) {
      scaleFactor = 1.1;
    } else if (dataLength > 10) {
      scaleFactor = 1.05;
    }

    return {
      width: `${Math.min(baseWidth * scaleFactor, 800)}px`,
      height: `${Math.min(baseHeight * scaleFactor, 700)}px`,
    };
  }, [displayData, width, height]);

  const visibleData = useMemo(() => {
    if (!displayData || displayData.length === 0) return [];

    const normalizedData = displayData.map((row) => {
      const flatRow: Record<string, any> = {
        id: row.rowId,
        name: row.rowName,
        slug: row.rowSlug,
        rowName: row.rowName,
      };

      if (row.rowData && Array.isArray(row.rowData)) {
        row.rowData.forEach((varData) => {
          if (varData.variableId) {
            let value = varData.variableValue;
            if (typeof value === "string") {
              const parsed = parseFloat(value);
              value = isNaN(parsed) ? value : parsed;
            }
            flatRow[varData.variableId] = value;
            if (varData.variableSlug) flatRow[varData.variableSlug] = value;
          }
        });
      }

      return flatRow;
    });

    return normalizedData;
  }, [displayData]);

  const columnKeys = useMemo(() => {
    if (!displayData || displayData.length === 0) return [areaNameKey];

    const variablesMap = new Map<
      string,
      { id: string; name: string; order: number | null }
    >();

    displayData.forEach((row) => {
      if (row.rowData && Array.isArray(row.rowData)) {
        row.rowData.forEach((varData) => {
          if (varData.variableId && !variablesMap.has(varData.variableId)) {
            variablesMap.set(varData.variableId, {
              id: varData.variableId,
              name: varData.variableName,
              order: varData.variableOrder ?? null,
            });
          }
        });
      }
    });

    // Sort by order
    const sortedVariables = Array.from(variablesMap.values()).sort((a, b) => {
      if (a.order !== null && b.order !== null) return a.order - b.order;
      if (a.order !== null) return -1;
      if (b.order !== null) return 1;
      return a.name.localeCompare(b.name);
    });

    if (displaySelectedVariables && displaySelectedVariables.length > 0) {
      const byId = new Map(sortedVariables.map((v) => [v.id, v]));
      const orderedSelected = displaySelectedVariables.filter((id) =>
        byId.has(id),
      );
      return [areaNameKey, ...orderedSelected];
    }

    return [
      areaNameKey,
      ...sortedVariables.map((v) => v.id).slice(0, maxVisibleColumns - 1),
    ];
  }, [displayData, areaNameKey, displaySelectedVariables, maxVisibleColumns]);

  // Create a map of variable ids to names
  const variableNamesMap = useMemo(() => {
    const map = new Map<string, string>();

    if (displayData && displayData.length > 0) {
      displayData.forEach((row) => {
        if (row.rowData && Array.isArray(row.rowData)) {
          row.rowData.forEach((varData) => {
            if (
              varData.variableId &&
              varData.variableName &&
              !map.has(varData.variableId)
            ) {
              map.set(varData.variableId, varData.variableName);
            }
          });
        }
      });
    }

    return map;
  }, [displayData]);

  // Derive y-axis title from first variable's units in data when prop not provided
  const derivedYAxisTitle = useMemo(() => {
    if (!displayData?.length) return null;
    const firstRow = displayData[0];
    const firstVar = firstRow?.rowData?.[0];
    return firstVar?.variableUnits ?? null;
  }, [displayData]);

  const effectiveYAxisTitle =
    displayYAxisTitle ?? derivedYAxisTitle ?? "Customers";

  // Filter and sort data
  const processedData = useMemo(() => {
    let result = [...visibleData];

    // Apply filter
    if (filter) {
      result = result.filter((row) =>
        row.name?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Apply sort
    if (sortColumn) {
      result.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal);
        const bStr = String(bVal);
        return sortDirection === "asc"
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      });
    }

    return result;
  }, [visibleData, filter, sortColumn, sortDirection]);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, pageIndex, pageSize]);

  const pageCount = Math.ceil(processedData.length / pageSize);

  const aggregationRowData = useMemo(() => {
    if (!displayAggregationRow) return null;

    const flatRow: Record<string, any> = {
      id: displayAggregationRow.rowId,
      name: displayAggregationRow.rowName,
      slug: displayAggregationRow.rowSlug,
      rowName: displayAggregationRow.rowName,
    };

    if (
      displayAggregationRow.rowData &&
      Array.isArray(displayAggregationRow.rowData)
    ) {
      displayAggregationRow.rowData.forEach((varData) => {
        if (varData.variableId) {
          flatRow[varData.variableId] = varData.variableValue;
        }
      });
    }

    return flatRow;
  }, [displayAggregationRow]);

  const boxPlotSeries = useMemo<BoxPlotSeries[]>(() => {
    if (!visibleData || visibleData.length === 0) return [];

    const boxPlotDataPoints: BoxPlotDataPoint[] = [];
    
    const dotSize = visibleData.length > 15 ? 7 : 11;

    visibleData.forEach((featureData) => {
      const name = featureData[areaNameKey] || featureData.name || "Unknown";

      let whiskerMin = 0,
        q1 = 0,
        median = 0,
        q3 = 0,
        whiskerMax = 0;
      let bestEstimate: number | null = null;

      const getValue = (keys: string[]): number => {
        for (const key of keys) {
          const val = featureData[key];
          if (val !== undefined && val !== null && !isNaN(Number(val))) {
            return parseFloat(val);
          }
        }
        return 0;
      };

      whiskerMin = getValue([
        "estimate10th",
        "estimate_10th",
        "estimate10",
        "Estimate 10th",
      ]);
      q1 = getValue([
        "estimate25th",
        "estimate_25th",
        "estimate25",
        "Estimate 25th",
      ]);
      median = getValue([
        "predicted_median",
        "predictedMedian",
        "median",
        "Predicted Median",
        "Estimate Median",
      ]);
      q3 = getValue([
        "estimate75th",
        "estimate_75th",
        "estimate75",
        "Estimate 75th",
      ]);
      whiskerMax = getValue([
        "estimate90th",
        "estimate_90th",
        "estimate90",
        "Estimate 90th",
      ]);

      const bestEstVal = getValue([
        "best_estimate",
        "bestEstimate",
        "Best Estimate",
      ]);
      if (bestEstVal > 0) bestEstimate = bestEstVal;

      if (
        whiskerMin === q1 &&
        q1 === median &&
        median === q3 &&
        q3 === whiskerMax &&
        whiskerMin !== 0
      ) {
        const value = whiskerMin;
        const spread = Math.abs(value) * 0.1 || 0.1;

        whiskerMin = value - spread;
        q1 = value - spread * 0.5;
        median = value;
        q3 = value + spread * 0.5;
        whiskerMax = value + spread;
      }

      const dataPoint: BoxPlotDataPoint = {
        x: name.replace("Urbint ", ""),
        y: [whiskerMin, q1, median, q3, whiskerMax],
      };

      if (bestEstimate !== null) {
        dataPoint.goals = [
          {
            value: bestEstimate,
            strokeWidth: 0,
            strokeHeight: dotSize,
            strokeLineCap: "round",
            strokeColor: "#f97316",
          },
        ];
      }

      boxPlotDataPoints.push(dataPoint);
    });

    const series: BoxPlotSeries[] = [
      {
        name: "Distribution",
        data: boxPlotDataPoints,
      },
    ];

    return series;
  }, [visibleData, areaNameKey]);

  // Format cell value for display
  const formatCellValue = useCallback((value: any): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") return value.toLocaleString();
    return String(value);
  }, []);

  // Get column display label (key is variableId or areaNameKey)
  const getColumnLabel = useCallback((key: string): string => {
    if (key === areaNameKey) return "Area";
    if (variableNamesMap.has(key)) return variableNamesMap.get(key)!;
    if (formatVariableName) return formatVariableName(key);
    return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }, [areaNameKey, variableNamesMap, formatVariableName]);

  // Handle sort toggle
  const handleSort = useCallback((key: string) => {
    setSortDirection((prev) => {
      if (sortColumn === key) {
        return prev === "asc" ? "desc" : "asc";
      }
      return "asc";
    });
    setSortColumn(key);
  }, [sortColumn]);

  const escapeCSVValue = useCallback((value: string): string => {
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }, []);

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    if (!processedData || processedData.length === 0) return;

    const csvLines: string[] = [];

    csvLines.push("# Report Information");
    csvLines.push(`Report Title,${escapeCSVValue(title)}`);

    if (displayCsvMetadata?.weatherModel) {
      csvLines.push(
        `Weather Model,${escapeCSVValue(displayCsvMetadata.weatherModel)}`
      );
    }

    if (displayCsvMetadata?.validDate) {
      const validDateStr = displayCsvMetadata.validDate.toLocaleDateString(
        "en-US",
        {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        },
      );
      csvLines.push(`Valid For,${escapeCSVValue(validDateStr)}`);
    }

    if (displayCsvMetadata?.modelRunDate) {
      const modelRunStr = `${displayCsvMetadata.modelRunDate.toLocaleDateString(
        "en-US",
        {
          month: "long",
          day: "numeric",
          year: "numeric",
        }
      )} ${displayCsvMetadata.modelRunDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })} Model Run`;
      csvLines.push(`Model Run,${escapeCSVValue(modelRunStr)}`);
    }

    if (displayCsvMetadata?.predictionType) {
      csvLines.push(
        `Prediction Type,${escapeCSVValue(displayCsvMetadata.predictionType)}`
      );
    }

    const variableNames = columnKeys
      .filter((key) => key !== areaNameKey)
      .map(getColumnLabel);
    if (variableNames.length > 0) {
      csvLines.push(
        `Variables Included,${escapeCSVValue(variableNames.join("; "))}`
      );
    }

    csvLines.push(
      `Generated At,${escapeCSVValue(new Date().toLocaleString())}`
    );
    csvLines.push("");

    const headers = columnKeys.map((key) =>
      escapeCSVValue(getColumnLabel(key))
    );
    csvLines.push(headers.join(","));

    processedData.forEach((row) => {
      const rowValues = columnKeys.map((key) =>
        escapeCSVValue(formatCellValue(row[key]))
      );
      csvLines.push(rowValues.join(","));
    });

    if (aggregationRowData) {
      const aggValues = columnKeys.map((key, index) => {
        if (index === 0) {
          const label = aggregationRowData.name
            ? `${aggregationRowData.name} (Total)`
            : "Total";
          return escapeCSVValue(label);
        }
        const value = aggregationRowData[key];
        return escapeCSVValue(
          value !== undefined && value !== null ? formatCellValue(value) : "-"
        );
      });
      csvLines.push(aggValues.join(","));
    }

    const csv = csvLines.join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${title.replace(/\s+/g, "_")}_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
  }, [processedData, columnKeys, areaNameKey, getColumnLabel, formatCellValue, aggregationRowData, title, displayCsvMetadata, escapeCSVValue]);

  // Render table content
  const renderTableContent = useCallback(() => (
    <div className="overflow-x-auto flex flex-col h-full">
      {/* Table controls */}
      <div className="px-3 flex items-center justify-between gap-2 py-2 flex-shrink-0">
        <Input
          className="flex-1 max-w-[180px] text-xs h-7"
          placeholder="Filter..."
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPageIndex(0);
          }}
        />

        {displayAvailableVariables.length > 0 && onSelectedVariablesChange && (
          <VariableSelectionDropdown
            availableVariables={displayAvailableVariables}
            selectedVariables={displaySelectedVariables || []}
            onSelectedVariablesChange={onSelectedVariablesChange}
            formatVariableName={formatVariableName}
          />
        )}

        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-7 flex-shrink-0"
          onClick={handleExportCSV}
          title="Download as CSV"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border">
        <ScrollArea className="min-h-0 flex-1">
          <div style={{ width: "max-content", minWidth: "100%" }}>
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                <TableRow>
                  {columnKeys.map((key) => (
                    <TableHead key={key} className="p-1">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort(key)}
                        className="p-0 h-auto font-medium text-xs flex items-center gap-1 w-full justify-start"
                      >
                        <span
                          className="break-words whitespace-normal text-left"
                          style={{ maxWidth: "94px" }}
                        >
                          {getColumnLabel(key)}
                        </span>
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 mt-0.5" />
                      </Button>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <TableRow
                      key={row.id || idx}
                      onClick={() => onRowClick && onRowClick(row)}
                      className={
                        onRowClick ? "cursor-pointer hover:bg-muted/50" : ""
                      }
                    >
                      {columnKeys.map((key) => (
                        <TableCell key={key} className="p-2 text-xs">
                          <div
                            className="break-words whitespace-normal overflow-hidden"
                            style={{ maxWidth: `${maxColumnWidth}px` }}
                          >
                            {formatCellValue(row[key])}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columnKeys.length}
                      className="h-24 text-center text-xs"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {paginatedData.length > 0 && aggregationRowData && (
                <TableFooter className="bg-muted border-t text-secondary-foreground">
                  <TableRow>
                    {columnKeys.map((key, index) => {
                      if (index === 0) {
                        return (
                          <TableCell
                            key={key}
                            className="p-2 text-xs font-medium"
                          >
                            {aggregationRowData.name
                              ? `${aggregationRowData.name} (Total)`
                              : "Total"}
                          </TableCell>
                        );
                      }

                      const value = aggregationRowData[key];

                      return (
                        <TableCell
                          key={key}
                          className="p-2 text-xs font-medium"
                        >
                          {value !== undefined && value !== null
                            ? formatCellValue(value)
                            : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <div className="flex flex-shrink-0 items-center justify-between border-t border-border bg-background px-3 py-1">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
              disabled={pageIndex === 0}
            >
              Previous
            </Button>
            <span className="text-xs">
              Page {pageIndex + 1} of {pageCount || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() =>
                setPageIndex((prev) => Math.min(pageCount - 1, prev + 1))
              }
              disabled={pageIndex >= pageCount - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  ), [filter, displayAvailableVariables, displaySelectedVariables, onSelectedVariablesChange, formatVariableName, handleExportCSV, columnKeys, handleSort, getColumnLabel, paginatedData, maxColumnWidth, formatCellValue, aggregationRowData, pageIndex, pageCount, onRowClick, setFilter, setPageIndex]);

  // Memoize tooltip formatter function
  const tooltipFormatter = useCallback(({
    seriesIndex,
    dataPointIndex,
    w,
  }: {
    seriesIndex: number;
    dataPointIndex: number;
    w: any;
  }) => {
    const chartData =
      w.globals.initialSeries[seriesIndex].data[dataPointIndex];
    const areaName = chartData.x;
    const [min, q1Val, medianVal, q3Val, max] = chartData.y;
    const formattedValues = {
      min: min.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      q1: q1Val.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      median: medianVal.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      q3: q3Val.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      max: max.toLocaleString(undefined, { maximumFractionDigits: 1 }),
    };

    const bestEst = chartData.goals?.[0]?.value;
    const formattedBestEstimate =
      bestEst !== undefined
        ? bestEst.toLocaleString(undefined, { maximumFractionDigits: 1 })
        : "N/A";

    return `
      <div style="padding: 6px; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); border: 1px solid #e5e7eb; max-width: 180px; font-size: 10px; background-color: white; color: #374151;">
        <div style="font-weight: bold; margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${areaName}</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px;">
          <span style="color: #6b7280;">Best Est:</span>
          <span style="font-weight: 500; color: #c2410c;">${formattedBestEstimate}</span>
          <span style="color: #6b7280;">10th:</span>
          <span style="font-weight: 500;">${formattedValues.min}</span>
          <span style="color: #6b7280;">25th:</span>
          <span style="font-weight: 500;">${formattedValues.q1}</span>
          <span style="color: #6b7280;">50th:</span>
          <span style="font-weight: 500;">${formattedValues.median}</span>
          <span style="color: #6b7280;">75th:</span>
          <span style="font-weight: 500;">${formattedValues.q3}</span>
          <span style="color: #6b7280;">90th:</span>
          <span style="font-weight: 500;">${formattedValues.max}</span>
        </div>
      </div>
    `;
  }, []);

  // Memoize box plot options to prevent unnecessary re-renders
  // NOTE: triggerChartRedraw is NOT in dependencies to keep toolbar stable during resize
  const boxPlotOptions = useMemo(() => {
    const dataPointCount = boxPlotSeries[0]?.data.length || 0;
    const isLargeDataset = dataPointCount > 15;
    const isVeryLargeDataset = dataPointCount > 30;
    
    let fontSize = "11px";
    let rotateAngle = -45;
    let maxHeight = 100;
    let hideOverlapping = false;
    
    if (isVeryLargeDataset) {
      fontSize = "8px";
      rotateAngle = -90;
      maxHeight = 150;
      hideOverlapping = true;
    } else if (isLargeDataset) {
      fontSize = "9px";
      rotateAngle = -60;
      maxHeight = 120;
      hideOverlapping = true;
    }

    return {
      chart: {
        id: "combinedBoxPlot",
        type: "boxPlot" as const,
        height: "100%",
        width: "100%",
        background: "transparent",
        animations: {
          enabled: false,
        },
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
          autoSelected: "zoom" as const,
        },
        zoom: {
          enabled: true,
          type: "xy" as const,
        },
        // In panel mode, shrink with the tray; floating mode uses manual redraw on resize handle
        redrawOnParentResize: panelMode,
        redrawOnWindowResize: panelMode,
      },
      plotOptions: {
        boxPlot: {
          colors: {
            upper: "#60a5fa",
            lower: "#1e3a5f",
          },
        },
      },
      colors: ["#3b82f6"],
      stroke: {
        colors: ["#374151"],
        width: 1,
      },
      xaxis: {
        categories:
          boxPlotSeries[0]?.data.map((d: BoxPlotDataPoint) => d.x) || [],
        labels: {
          show: true,
          style: {
            fontSize: fontSize,
            colors: "#374151",
          },
          trim: true,
          hideOverlappingLabels: hideOverlapping,
          rotateAlways: true,
          rotate: rotateAngle,
          maxHeight: maxHeight,
          offsetX: isVeryLargeDataset ? -5 : 0,
          offsetY: isVeryLargeDataset ? 0 : 5,
        },
        tooltip: {
          enabled: false,
        },
        axisBorder: {
          show: true,
          color: "#9ca3af",
        },
        axisTicks: {
          show: true,
          color: "#9ca3af",
        },
      },
      yaxis: {
        title: effectiveYAxisTitle
          ? {
              text: effectiveYAxisTitle,
              style: {
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
              },
            }
          : undefined,
        labels: {
          style: {
            fontSize: "11px",
            colors: ["#374151"],
          },
          formatter: (value: number) => {
            return Math.round(value).toLocaleString();
          },
        },
        axisBorder: {
          show: true,
          color: "#9ca3af",
        },
        axisTicks: {
          show: true,
          color: "#9ca3af",
        },
      },
      grid: {
        borderColor: "#d1d5db",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      tooltip: {
        enabled: true,
        x: {
          show: false,
        },
        custom: tooltipFormatter,
        style: {
          fontSize: "10px",
        },
      },
      responsive: [
        {
          breakpoint: 1000,
          options: {
            chart: {
              height: "100%",
            },
          },
        },
      ],
    };
  }, [boxPlotSeries, effectiveYAxisTitle, tooltipFormatter, panelMode]);

  // Render box plot content - smooth blur and fade during resize
  const renderBoxPlotContent = useCallback((isResizing: boolean) => {
    // Detect resize end (was resizing, now not)
    const wasResizing = isResizingRef.current;
    
    // Cache the chart data when resize starts
    if (isResizing && !wasResizing) {
      // Resize just started - cache current state
      cachedChartDataRef.current = {
        series: boxPlotSeries,
        options: boxPlotOptions,
      };
    }
    
    // When resize ends, enable chart redraw at new size
    if (!isResizing && wasResizing) {
      // Use setTimeout to ensure dimensions are applied before redraw
      setTimeout(() => {
        setTriggerChartRedraw(true);
        // Reset the flag after a moment to prevent excessive redraws
        setTimeout(() => {
          setTriggerChartRedraw(false);
          cachedChartDataRef.current = null;
        }, 200);
      }, 50);
    }
    
    isResizingRef.current = isResizing;
    
    // Use cached data during resize, fresh data otherwise
    const chartSeries = isResizing && cachedChartDataRef.current 
      ? cachedChartDataRef.current.series 
      : boxPlotSeries;
    const chartOptions = isResizing && cachedChartDataRef.current 
      ? cachedChartDataRef.current.options 
      : boxPlotOptions;

    return (
      <div className="relative flex h-full min-h-0 flex-col overflow-hidden p-2">
        <div 
          className="relative min-h-0 flex-1"
          style={{
            filter: isResizing ? "blur(2px)" : "blur(0px)",
            opacity: isResizing ? 0.6 : 1,
            transition: `filter 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)`,
            willChange: "filter, opacity",
            transform: "translateZ(0)", // Force GPU layer
          }}
        >
          <BoxAndWhiskerPlot
            key={`chart-${triggerChartRedraw}-${dataChangeKey}`}
            series={chartSeries}
            options={chartOptions}
            height={"100%"}
            width={"100%"}
            fillContainer={panelMode}
          />
        </div>
        {/* Overlay during resize to prevent interactions */}
        {isResizing && (
          <div 
            className="absolute inset-0 bg-transparent" 
            style={{ pointerEvents: "all", zIndex: 50 }}
          />
        )}
      </div>
    );
  }, [boxPlotSeries, boxPlotOptions, triggerChartRedraw, dataChangeKey, panelMode]);

  return (
    <AbstractInset
      data={data}
      loading={loading}
      error={error}
      position={position}
      width={panelMode ? "100%" : responsiveDimensions.width}
      height={panelMode ? "100%" : responsiveDimensions.height}
      title={title}
      subtitle={displaySubtitle}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      className={className}
      containerId="map-combined-forecast-inset-container"
      isDraggable={isDraggable}
      panelMode={panelMode}
      infoTooltip={
        <div className="text-sm space-y-2">
          <p className="font-medium">Forecast Overview</p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            <li>
              <strong>At a Glance</strong>: Table view showing forecast values
              for each area
            </li>
            <li>
              <strong>Uncertainty</strong>: Box plot showing the distribution of
              forecast values
            </li>
          </ul>
          {!panelMode && (
            <p className="text-xs text-muted-foreground pt-1">
              Tip: Drag the header to move this panel, or resize from the corner.
            </p>
          )}
        </div>
      }
    >
      {({ isResizing }: InsetState) => (
        <>
          {/* Error message */}
          {error && (
            <div className="p-3 bg-destructive/10 border-b border-destructive/20">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {visibleData.length === 0 ? (
            <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center px-10 py-16 text-center">
              <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
                Loading...
              </p>
            </div>
          ) : (
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as "table" | "boxplot")
              }
              className="relative flex h-full min-h-0 flex-col overflow-hidden"
              aria-busy={loading}
            >
              {shouldPreserveInsetContent && (
                <div
                  className="absolute inset-0 z-20 bg-background/5 backdrop-blur-[0.5px]"
                  aria-hidden
                />
              )}
              <TabsList className="mx-3 mt-2 grid w-auto grid-cols-2 h-10">
                <TabsTrigger
                  value="table"
                  className="text-xs flex items-center gap-1.5 h-7"
                  onClick={() => trackRumAction('forecast_tab_switched', { tab: 'table' })}
                >
                  <Table2 className="h-3.5 w-3.5" />
                  At a Glance
                </TabsTrigger>
                <TabsTrigger
                  value="boxplot"
                  className="text-xs flex items-center gap-1.5 h-7"
                  onClick={() => trackRumAction('forecast_tab_switched', { tab: 'boxplot' })}
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Uncertainty
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="table"
                className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
              >
                {renderTableContent()}
              </TabsContent>

              <TabsContent
                value="boxplot"
                className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
              >
                {boxPlotSeries.length === 0 ? (
                  <div className="flex min-h-[180px] flex-1 flex-col items-center justify-center px-10 py-16 text-center">
                    <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
                      No data available
                    </p>
                  </div>
                ) : (
                  renderBoxPlotContent(isResizing)
                )}
              </TabsContent>
            </Tabs>
          )}
        </>
      )}
    </AbstractInset>
  );
};

export { MapCombinedForecastInset };
export type { MapCombinedForecastInsetProps };
